import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Seo from "@/components/Seo";
import { verifyEmail } from "@/lib/customerAuth";

/**
 * /account/verify (Design Specification Section 12.3). One field for the code, paste allowed, no auto-advance
 * boxes, no timer. No resend link: the endpoint does not exist yet (open item), and registering again does
 * not work as a resend because the server answers with the existing-account message either way.
 */
export default function VerifyEmail() {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state && location.state.email;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await verifyEmail(code.trim());
      setDone(true);
    } catch {
      setError("That code is invalid or has expired.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[480px] mx-auto px-6 py-16" data-testid="account-verify-page">
        <Seo title="Confirm your email" noindex path="/account/verify" />
        <h1 tabIndex={-1} className="font-display text-3xl font-extrabold tracking-tight text-[#0A2540] mb-4">Email confirmed.</h1>
        <p role="status" data-testid="verify-success">Your email address is confirmed.</p>
        <Link to="/account/login" className="btn-green mt-6 inline-flex" data-testid="verify-go-signin">Sign in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-[480px] mx-auto px-6 py-16" data-testid="account-verify-page">
      <Seo title="Confirm your email" noindex path="/account/verify" />
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-[#0A2540] mb-3">Confirm your email</h1>
      <p className="text-slate-600 mb-6">
        {email ? `Enter the code we sent to ${email}.` : "Enter the code we sent to your email."}
      </p>

      {error && (
        <p role="alert" className="border-2 border-[#B91C1C] bg-[#FEF2F2] text-[#B91C1C] rounded-xl p-3 mb-5" data-testid="verify-error">
          {error}
        </p>
      )}

      <form onSubmit={submit} noValidate className="space-y-5" data-testid="verify-form">
        <div>
          <label htmlFor="verify-code" className="text-overline text-zinc-500 mb-1 block">Confirmation code</label>
          <input
            id="verify-code"
            type="text"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="input-brutal"
            data-testid="field-code"
          />
        </div>
        <button type="submit" disabled={submitting} aria-busy={submitting} className="btn-green w-full" data-testid="verify-submit">
          {submitting ? "Confirming…" : "Confirm email"}
        </button>
      </form>
    </div>
  );
}
